import { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import Slide from '@mui/material/Slide';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import profile from '@benniehealth/core-js/store/profile';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import { useTranslation } from '@app/i18n';
import IconInfo from '@app/images/svg/icons/icon_info.svg';
import IconUser from '@app/images/svg/icons/icon_user.svg';
import { coreClient as client } from '@app/helpers/requests/client';

const PREFIX = 'index';

const classes = {
  container: `${PREFIX}-container`,
  icon: `${PREFIX}-icon`,
  inner: `${PREFIX}-inner`,
  button: `${PREFIX}-button`,
  sliderWrapper: `${PREFIX}-sliderWrapper`,
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.container}`]: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  [`& .${classes.icon}`]: {
    position: 'absolute',
    right: '16px',
    top: '16px',
    zIndex: 3,
  },
  [`& .${classes.inner}`]: {
    backgroundColor: theme.palette.primary.main,
  },
  [`& .${classes.button}`]: {
    color: theme.palette.background.default,
    marginLeft: '24px',
  },
  [`& .${classes.sliderWrapper}`]: {
    position: `fixed`,
    width: `100%`,
    right: 0,
    zIndex: 9999,
    top: 0,
  },
}));

const ImpersonationControl = ({
  isImpersonationSession,
  employeeFullName,
  companyName,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isHidden, setIsHidden] = useState(true);

  if (!isImpersonationSession) {
    return null;
  }

  const handleClick = async () => {
    setIsHidden(true);

    await client.put(`/v1/admin/impersonations`);

    navigate('/');

    window.location.reload();
  };

  return (
    <Root>
      <Box className={classes.icon}>
        <IconButton onClick={() => setIsHidden(false)} size="large">
          <SvgIcon component={IconInfo} />
        </IconButton>
      </Box>

      <Box className={classes.sliderWrapper}>
        <Slide
          mountOnEnter
          unmountOnExit
          direction="left"
          in={!isHidden}
          timeout={300}
        >
          <Box className={classes.container}>
            <ClickAwayListener onClickAway={() => setIsHidden(true)}>
              <Box
                display="flex"
                alignItems="center"
                p={3}
                className={classes.inner}
              >
                <Box display="flex" flexDirection="column">
                  <Typography color="background.default" variant="labelRegular">
                    {employeeFullName}
                  </Typography>

                  <Typography variant="labelRegular" color="background.default">
                    {companyName}
                  </Typography>
                </Box>

                <Button
                  className={classes.button}
                  onClick={handleClick}
                  startIcon={<SvgIcon color="inherit" component={IconUser} />}
                >
                  {t('impersonationControl.switchUser')}
                </Button>
              </Box>
            </ClickAwayListener>
          </Box>
        </Slide>
      </Box>
    </Root>
  );
};

ImpersonationControl.propTypes = {
  isImpersonationSession: PropTypes.bool,
  employeeFullName: PropTypes.string,
  companyName: PropTypes.string,
};

const mapStateToProps = state => ({
  isImpersonationSession: state.companyAccesses.isImpersonationSession,
  employeeFullName: profile.selectors.getEmployeeFullName(state),
  companyName: profile.selectors.getCompanyName(state),
});

const withConnect = connect(mapStateToProps);

export default compose(withConnect)(ImpersonationControl);
